import type { AttendanceDashboardView, AttendanceReportRules, AttendanceShiftView } from 'shared-models/browser';

export function attendancePayableTotal(salary: number, tipsCredit: number, tipsCash: number, rules: AttendanceReportRules): number {
  return Math.round((salary + (rules.includeCreditTipsInPay ? tipsCredit : 0) + (rules.includeCashTipsInPay ? tipsCash : 0) + Number.EPSILON) * 100) / 100;
}

type AttendancePayMinute = {
  salary: number;
  departmentName: string;
  dayBonusRate: 0 | 0.5 | 1;
  overtimeBonusRate: 0 | 0.25 | 0.5;
};

export function formatOngoingShiftDuration(clockedInAt: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - Date.parse(clockedInAt)) / 60000));
  if (!Number.isFinite(minutes)) return '—';
  return `${Math.floor(minutes / 60)} ש' ${minutes % 60} ד'`;
}

export function resolveAttendanceReportRules(rules: AttendanceReportRules, employeeId: number | null): AttendanceReportRules {
  return rules.employeeRuleSets?.find((group) => employeeId !== null && group.employeeIds.includes(employeeId))?.rules ?? rules;
}

export function buildAttendancePayMinutes(
  shifts: AttendanceShiftView[],
  fallbackHourlyBase: number,
  rules: AttendanceReportRules,
  isNightWindowMinute: (date: Date) => boolean,
  resolveDayBonusRate: (date: Date) => 0 | 0.5 | 1,
): AttendancePayMinute[] {
  const threshold125Minutes = Math.max(0, Math.floor(rules.overtime125StartHours * 60));
  const threshold150Minutes = Math.max(threshold125Minutes, Math.floor(rules.overtime150StartHours * 60));
  const result: AttendancePayMinute[] = [];
  for (const shift of shifts) {
    if (shift.isOpen || !shift.clockOutAt) continue;
    const startMs = Date.parse(shift.clockInAt);
    const endMs = Date.parse(shift.clockOutAt);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    const maxMinutes = Math.max(0, Math.floor((endMs - startMs) / 60000));
    const shiftMinutes = Math.max(0, Math.floor(shift.durationMinutes));
    const minutesToCheck = Math.min(maxMinutes, shiftMinutes || maxMinutes);
    const dates = Array.from({ length: minutesToCheck }, (_, minuteIndex) => new Date(startMs + minuteIndex * 60000));
    const qualifiesForNightOvertime = rules.useNightShiftOvertimeRules
      && dates.filter(isNightWindowMinute).length >= 120;
    const threshold125 = qualifiesForNightOvertime ? Math.max(0, threshold125Minutes - 60) : threshold125Minutes;
    const salary = typeof shift.hourlySalary === 'number' ? shift.hourlySalary : fallbackHourlyBase;
    dates.forEach((date, minuteIndex) => {
      result.push({
        salary,
        departmentName: shift.departmentName ?? 'כללי',
        dayBonusRate: resolveDayBonusRate(date),
        overtimeBonusRate: minuteIndex >= threshold150Minutes ? 0.5 : minuteIndex >= threshold125 ? 0.25 : 0,
      });
    });
  }
  return result;
}

export function applyAttendanceShiftSnapshot(dashboard: AttendanceDashboardView, shift: AttendanceShiftView): AttendanceDashboardView {
  const previous = dashboard.shifts.find((row) => row.clockInActionId === shift.clockInActionId);
  if (!previous || !shift.updatedAt || Date.parse(shift.updatedAt) < Date.parse(previous.updatedAt ?? '1970-01-01')) return dashboard;
  const shifts = dashboard.shifts.map((row) => row.clockInActionId === shift.clockInActionId ? shift : row);
  return { ...dashboard, shifts, openShifts: shifts.filter((row) => row.isOpen),
    actions: dashboard.actions.map((action) => action.id === shift.clockInActionId
      ? { ...action, departmentName: shift.departmentName, baseHourlySalary: shift.hourlySalary, updatedAt: shift.updatedAt! } : action),
    totalHours: Math.round((shifts.reduce((sum, row) => sum + row.durationHours, 0) + Number.EPSILON) * 100) / 100,
    totalSalary: Math.round((shifts.reduce((sum, row) => sum + row.shiftSalary, 0) + Number.EPSILON) * 100) / 100,
  };
}